import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";

/**
 * Email verification used to block registration. New accounts had to request a
 * code and enter it before they could continue, which added friction before
 * anyone could try Peak. Signup no longer waits on that check. The same
 * sendVerificationCode / verifyEmailCode callables now live here, so people
 * can confirm the address from Settings once they already have an account.
 */
interface VerifyEmailSettingsProps {
  email: string;
  verified: boolean;
  onVerified: () => void;
}

const callableMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.replace(/^Firebase:\s*/i, "").trim();
  }
  return fallback;
};

const VerifyEmailSettings: React.FC<VerifyEmailSettingsProps> = ({ email, verified, onVerified }) => {
  const { currentUser } = useAuth();
  const [isVerified, setIsVerified] = useState(verified);
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsVerified(verified);
  }, [verified]);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = window.setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => window.clearInterval(interval);
  }, [timer]);

  if (!email) return null;

  const sendCode = async () => {
    const accountEmail = currentUser?.email?.trim() || email.trim();
    setSending(true);
    setError(null);
    try {
      const sendCodeFunction = httpsCallable(functions, "sendVerificationCode");
      await sendCodeFunction({ email: accountEmail });
      setShowCodeInput(true);
      setTimer(60);
    } catch (sendError) {
      setError(callableMessage(sendError, "Failed to send code. Try again."));
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    const accountEmail = currentUser?.email?.trim();
    if (code.length !== 6 || !currentUser || !accountEmail) {
      setError("Please enter a valid 6-digit code.");
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      // The callable is the only writer of isEmailVerified. It rejects a code
      // whose address is not the email on this auth account.
      const verifyCodeFunction = httpsCallable(functions, "verifyEmailCode");
      const result = await verifyCodeFunction({ email: accountEmail, code });
      const success = Boolean((result.data as { success?: boolean } | undefined)?.success);
      if (!success) {
        setError("Invalid or expired code.");
        return;
      }
      const fresh = await userService.getUserProfile(currentUser.uid);
      if (!fresh.userData?.isEmailVerified) {
        setError("Could not confirm verification. Try again.");
        return;
      }
      setIsVerified(true);
      setShowCodeInput(false);
      onVerified();
    } catch (verifyError) {
      setError(callableMessage(verifyError, "Invalid or expired code."));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mb-6">
      <h3 className="text-lg font-semibold text-base-content">Email verification</h3>
      {isVerified ? (
        <p className="text-sm text-base-content/70 mt-2">
          <span className="text-green-700 dark:text-green-400 font-medium">Verified.</span> {email}
        </p>
      ) : (
        <>
          <p className="text-sm text-base-content/70 mt-2">
            Confirm {email} when you are ready. This used to be required during signup.
          </p>
          <button
            type="button"
            onClick={sendCode}
            disabled={sending || timer > 0}
            className="mt-4 bg-base-300 w-full text-base-content font-grotesk px-4 py-2 rounded-md hover:opacity-80 shadow-none transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending..." : timer > 0 ? `Resend code in ${timer}s` : showCodeInput ? "Resend code" : "Send verification code"}
          </button>
          {showCodeInput && (
            <div className="mt-4 flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                aria-label="Verification code"
                className="input input-bordered w-full text-center tracking-widest font-mono"
                placeholder="000000"
                value={code}
                disabled={verifying}
                onChange={(event) => {
                  setCode(event.target.value.replace(/[^0-9]/g, ""));
                  setError(null);
                }}
              />
              <button
                type="button"
                onClick={verifyCode}
                disabled={code.length !== 6 || verifying}
                className="btn btn-primary min-w-[100px]"
              >
                {verifying ? "Checking..." : "Verify"}
              </button>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-700 dark:text-red-400 mt-3" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default VerifyEmailSettings;
